import { useState } from 'react'

import { Box, Text, useClassNames } from '@factorialco/deprecated-design-system'
import { Button } from '@factorialco/factorial-one-react'
import { Ai } from '@factorialco/factorial-one-react/icons/app'

import i18n from 'modules/core/lib/i18n'

import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import { useUnassignedShifts } from 'modules/shift_management/hooks/useUnassignedShifts'
import { ShiftManagementFeatureFlags } from 'modules/shift_management/lib/consts/ShiftManagementFeatureFlags'
import type { View } from 'modules/shift_management/lib/types'
import { overrideGammaDimensions } from 'modules/shift_management/store/internal/models/View'
import { useGetPlannedBreaks } from 'modules/time_planning/hooks/plannedBreaks/useGetPlannedBreaks'

import { Column, ColumnsType } from '../../utils/buildColumns'
import * as stylesRefs from '../index.vanilla.css'
import { AutoScheduleModal } from './AutoScheduleModal'
import CoverageShiftsCell from './CoverageShiftsCell'

interface CoverageSectionProps {
  columns: ColumnsType
  view: View
  contractVersions: ContractVersions
  setIsSubscribed: (isSubscribed: boolean) => void
  resizeHeight: number
}

const Body = ({
  columns,
  view,
  contractVersions,
  setIsSubscribed,
  resizeHeight,
}: CoverageSectionProps) => {
  const [showAutoScheduleModal, setShowAutoScheduleModal] = useState<boolean>(false)
  const cx = useClassNames(stylesRefs)
  const cells = columns.slice(1)
  const columnMinWidth = view.columnMinWidth
  const dimensions = overrideGammaDimensions()
  // eslint-disable-next-line no-type-assertion/no-type-assertion
  const width = dimensions[columnMinWidth as keyof typeof dimensions]
  const [unassignedShifts] = useUnassignedShifts()
  const defaultShiftIds = unassignedShifts.reduce((acc: number[], { defaultShiftId }) => {
    if (defaultShiftId) acc.push(defaultShiftId)
    return acc
  }, [])
  const { plannedBreaksCollection } = useGetPlannedBreaks({
    variables: { defaultShiftIds },
    skip: !defaultShiftIds.length,
  })
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const isAutomationEnabled = hasFeatureFlag(
    ShiftManagementFeatureFlags.DEV_SHIFT_MANAGEMENT_AUTOMATION
  )

  return (
    <>
      {showAutoScheduleModal && isAutomationEnabled && (
        <AutoScheduleModal
          contractVersions={contractVersions}
          onClose={() => setShowAutoScheduleModal(false)}
          setIsSubscribed={setIsSubscribed}
        />
      )}
      <Box width="full" className={cx('gridHeader')}>
        <Box
          background="grey100"
          className={cx('sticky')}
          border={{
            right: {
              width: 's1',
              style: 'solid',
              color: 'grey300',
            },
          }}
          padding="s16"
          flexDirection="column"
          style={{
            height: `${resizeHeight || 140}px`,
          }}
          minHeight="s140"
          maxHeight="s400"
          gap="s16"
        >
          {isAutomationEnabled && (
            <>
              <Text.Normal>
                {i18n.t('shift_management.coverage_section.automation.set_workplace')}
              </Text.Normal>
              <Button
                icon={Ai}
                label={i18n.t('shift_management.coverage_section.automation.button')}
                onClick={() => setShowAutoScheduleModal(true)}
                size="md"
                variant="outline"
                disabled={unassignedShifts.length <= 0}
              />
            </>
          )}
        </Box>
        <Box
          className={cx('scrollable')}
          minHeight="s140"
          style={{
            height: `${resizeHeight || 140}px`,
            maxHeight: '400px',
            gridTemplateColumns: `repeat(${view.dates.length}, minmax(${width}, 1fr))`,
          }}
        >
          {cells.map((heading: Column, index: number) => (
            <CoverageShiftsCell
              date={heading.date}
              key={index}
              plannedBreaks={plannedBreaksCollection.active}
              contractVersions={contractVersions}
            />
          ))}
        </Box>
      </Box>
    </>
  )
}

export default Body
